import { getIssueDateToFilter } from '../../../components/utils/categorizeIssues'
import { getIssueOfferSize } from '../../issues/utils'
import { Advisor, EnrichedIssueEntity, IssueEntity } from '../../../types/IssueEntity'

export type AdvisorTypeKey = 'financial_advisor' | 'legal_advisor' | 'issuing_agent'

export type SegmentationType = 'none' | 'issueType' | 'offerSize' | 'marketplace' | 'year'

export type IssueTypeSegment = 'FE' | 'RE' | 'IPO' | 'AE'

export const ADVISOR_TYPE_OPTIONS: { id: AdvisorTypeKey; label: string }[] = [
    { id: 'financial_advisor', label: 'Finansiella rådgivare' },
    { id: 'legal_advisor', label: 'Legala rådgivare' },
    { id: 'issuing_agent', label: 'Emissionsinstitut' },
]

export const SEGMENTATION_OPTIONS: { id: SegmentationType; label: string }[] = [
    { id: 'none', label: 'Ingen segmentering' },
    { id: 'issueType', label: 'Emissionstyp' },
    { id: 'offerSize', label: 'Emissionsstorlek' },
    { id: 'marketplace', label: 'Marknadsplats' },
    { id: 'year', label: 'Årtal' },
]

export const OFFER_SIZE_SEGMENTS = ['0–50 MSEK', '50–200 MSEK', '200–1 000 MSEK', '1 000+ MSEK'] as const

export const ISSUE_TYPE_SEGMENTS: IssueTypeSegment[] = ['FE', 'RE', 'IPO', 'AE']

const ADVISOR_FIELD_BY_TYPE: Record<AdvisorTypeKey, keyof IssueEntity> = {
    financial_advisor: 'financialAdvisors',
    legal_advisor: 'legalAdvisors',
    issuing_agent: 'issuingAgents',
}

export type AdvisorStatisticRow = {
    advisorId: string
    name: string
    segmentCounts: Record<string, number>
    total: number
}

export const getIssueTypeSegment = (issue: IssueEntity): IssueTypeSegment | null => {
    switch (issue.type) {
        case 'Rights issue':
            return 'FE'
        case 'Directed issue':
        case 'ABB Directed issue':
            return 'RE'
        case 'IPO':
            return 'IPO'
        case 'Non-cash issue':
            return 'AE'
        default:
            return null
    }
}

export const getOfferSizeMsek = (issue: IssueEntity) =>
    Math.round(getIssueOfferSize(issue as EnrichedIssueEntity) / 1_000_000)

export const getOfferSizeSegment = (msek: number): (typeof OFFER_SIZE_SEGMENTS)[number] => {
    if (msek < 50) return '0–50 MSEK'
    if (msek < 200) return '50–200 MSEK'
    if (msek < 1000) return '200–1 000 MSEK'
    return '1 000+ MSEK'
}

const getIssueYearSegment = (issue: IssueEntity): string | null => {
    const dateStr = getIssueDateToFilter(issue)
    if (!dateStr) return null

    return new Date(dateStr).getUTCFullYear().toString()
}

const normalizeMarketplaceName = (marketplaceName: string | null): string | null => {
    if (!marketplaceName) return null

    const normalized = marketplaceName.trim()
    const lowercase = normalized.toLowerCase()

    if (lowercase.includes('omx')) return 'NASDAQ'
    if (lowercase.includes('nasdaq')) return 'NASDAQ'
    if (lowercase.includes('first north')) return 'First North'
    if (lowercase.includes('ngm')) return 'NGM'
    if (lowercase.includes('spotlight')) return 'Spotlight'

    return normalized
}

export const getSegmentKey = (issue: IssueEntity, segmentation: SegmentationType): string | null => {
    switch (segmentation) {
        case 'none':
            return 'total'
        case 'issueType':
            return getIssueTypeSegment(issue)
        case 'offerSize':
            return getOfferSizeSegment(getOfferSizeMsek(issue))
        case 'marketplace':
            return normalizeMarketplaceName(issue.marketplaceName)
        case 'year':
            return getIssueYearSegment(issue)
    }
}

const getAdvisorsForIssue = (issue: IssueEntity, selectedTypes: AdvisorTypeKey[]): Advisor[] =>
    selectedTypes.flatMap((type) => {
        const field = ADVISOR_FIELD_BY_TYPE[type]
        const advisors = issue[field]

        if (!Array.isArray(advisors)) return []

        return advisors as Advisor[]
    })

export const buildAdvisorStatistics = (
    issues: IssueEntity[],
    selectedAdvisorTypes: AdvisorTypeKey[],
    segmentation: SegmentationType,
): AdvisorStatisticRow[] => {
    const byAdvisorId = new Map<string, AdvisorStatisticRow>()

    for (const issue of issues) {
        if (segmentation === 'issueType' && getIssueTypeSegment(issue) === null) {
            continue
        }

        const segmentKey = getSegmentKey(issue, segmentation)
        if (segmentKey === null) continue

        for (const advisor of getAdvisorsForIssue(issue, selectedAdvisorTypes)) {
            const existing =
                byAdvisorId.get(advisor.advisorId) ??
                ({
                    advisorId: advisor.advisorId,
                    name: advisor.name?.trim() || 'Ej namngiven',
                    segmentCounts: {},
                    total: 0,
                } satisfies AdvisorStatisticRow)

            existing.segmentCounts[segmentKey] = (existing.segmentCounts[segmentKey] ?? 0) + 1
            existing.total += 1
            byAdvisorId.set(advisor.advisorId, existing)
        }
    }

    return [...byAdvisorId.values()]
}

export const getSegmentKeysInOrder = (rows: AdvisorStatisticRow[], segmentation: SegmentationType): string[] => {
    if (segmentation === 'none') return ['total']

    if (segmentation === 'issueType') return [...ISSUE_TYPE_SEGMENTS]

    if (segmentation === 'offerSize') return [...OFFER_SIZE_SEGMENTS]

    const keys = new Set<string>()
    rows.forEach((row) => {
        Object.keys(row.segmentCounts).forEach((key) => keys.add(key))
    })

    if (segmentation === 'year') {
        return [...keys].sort((a, b) => Number(a) - Number(b))
    }

    if (segmentation === 'marketplace') {
        const preferredOrder = ['NASDAQ', 'First North', 'Spotlight', 'NGM']
        const sortedPreferred = preferredOrder.filter((key) => keys.has(key))
        const others = [...keys].filter((key) => !preferredOrder.includes(key)).sort((a, b) => a.localeCompare(b, 'sv'))
        return [...sortedPreferred, ...others]
    }

    return [...keys].sort((a, b) => a.localeCompare(b, 'sv'))
}

export type ChartDisplayRow = {
    id: string
    name: string
    segmentCounts: Record<string, number>
}

export const prepareChartDisplay = (rows: AdvisorStatisticRow[], maxAdvisors: number): ChartDisplayRow[] => {
    const sorted = [...rows].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'sv'))
    return sorted.slice(0, maxAdvisors).map(({ advisorId, name, segmentCounts }) => ({
        id: advisorId,
        name,
        segmentCounts,
    }))
}
